// Logger utility for better error tracking and debugging
interface ErrorInfo {
  timestamp: string;
  level: string;
  message: string;
  stack?: string;
  context: {
    userAgent?: string;
    url?: string;
    timestamp: number;
    [key: string]: any;
  };
}

interface Context {
  [key: string]: any;
}

class Logger {
  private isDevelopment: boolean;
  private isProduction: boolean;

  constructor() {
    this.isDevelopment = process.env.NODE_ENV === 'development';
    this.isProduction = process.env.NODE_ENV === 'production';
  }

  // Log levels
  static LEVELS = {
    ERROR: 'ERROR',
    WARN: 'WARN',
    INFO: 'INFO',
    DEBUG: 'DEBUG'
  } as const;

  // Format error with context
  private formatError(error: Error | any, context: Context = {}): ErrorInfo {
    const errorInfo: ErrorInfo = {
      timestamp: new Date().toISOString(),
      level: Logger.LEVELS.ERROR,
      message: error?.message || 'Unknown error',
      stack: error?.stack,
      context: {
        ...context,
        userAgent: typeof navigator !== 'undefined' ? navigator?.userAgent : undefined,
        url: typeof window !== 'undefined' ? window?.location?.href : undefined,
        timestamp: Date.now()
      }
    };

    return errorInfo;
  }

  // Error logging with context
  error(error: Error | any, context: Context = {}): void {
    const errorInfo = this.formatError(error, context);
    
    if (this.isDevelopment) {
      console.error('🚨 ERROR:', {
        message: errorInfo.message,
        context: errorInfo.context,
        stack: errorInfo.stack
      });
    } else {
      // In production, you might want to send to a logging service
      console.error('ERROR:', errorInfo.message, errorInfo.context);
    }

    // You can add additional error reporting here (e.g., Sentry, LogRocket, etc.)
    // this.reportToService(errorInfo);
  }

  // Warning logging
  warn(message: string, context: Context = {}): void {
    if (this.isDevelopment) {
      console.warn('⚠️ WARNING:', message, context);
    }
  }

  // Info logging (only in development)
  info(message: string, context: Context = {}): void {
    if (this.isDevelopment) {
      console.info('ℹ️ INFO:', message, context);
    }
  }

  // Debug logging (only in development)
  debug(message: string, context: Context = {}): void {
    if (this.isDevelopment) {
      console.debug('🔍 DEBUG:', message, context);
    }
  }

  // API error logging
  apiError(endpoint: string, error: Error | any, requestData: Context = {}): void {
    this.error(error, {
      type: 'API_ERROR',
      endpoint,
      requestData,
      status: error?.response?.status,
      statusText: error?.response?.statusText
    });
  }

  // Wallet error logging
  walletError(operation: string, error: Error | any, walletData: Context = {}): void {
    this.error(error, {
      type: 'WALLET_ERROR',
      operation,
      walletData,
      walletType: walletData.walletType
    });
  }

  // Transaction error logging
  transactionError(txHash: string, error: Error | any, transactionData: Context = {}): void {
    this.error(error, {
      type: 'TRANSACTION_ERROR',
      txHash,
      transactionData
    });
  }

  // Component error logging
  componentError(componentName: string, error: Error | any, props: Context = {}): void {
    this.error(error, {
      type: 'COMPONENT_ERROR',
      componentName,
      props: Object.keys(props) // Only log prop names for privacy
    });
  }

  // Service error logging
  serviceError(serviceName: string, method: string, error: Error | any, params: Context = {}): void {
    this.error(error, {
      type: 'SERVICE_ERROR',
      serviceName,
      method,
      params
    });
  }

  // Report to external service (placeholder for future implementation)
  private reportToService(errorInfo: ErrorInfo): void {
    // Example: Send to Sentry, LogRocket, or custom logging service
    // if (this.isProduction) {
    //   // Send errorInfo to your logging service
    // }
  }
}

// Create singleton instance
const logger = new Logger();

export default logger; 